import './oracle-text-condition.css';

import h from 'hyperscript';
import isEqual from 'lodash-es/isEqual';

import { createSlider } from 'ui/slider/slider';

import { on } from 'cadenza/utils/event-util';
import { OracleTextConditionApi } from 'cadenza/api-client/conditions/oracle-text-condition-api';

import { ConditionElement } from '../condition-element';

import i18n from './messages.properties';

const MIN_ACCURACY = 40;
const MAX_ACCURACY = 80;

const SLIDER_STEP = 10;

class OracleTextCondition extends ConditionElement {

  #wasChanged;
  #currentScore;
  #latestSetValuesPromise; // for keeping consistency for other conditions, see onConditionChange

  connectedCallback () {
    this._oracleTextExpression = h('input.oracleTextExpression.form-control', {
      contentEditable: true,
      placeholder: i18n('search.placeholder')
    });

    const sliderElement = h('div.oracleTextSlider');

    this._createSlider(sliderElement);

    this.append(this._oracleTextExpression, h('.slider-wrapper', sliderElement));

    this.conditionApi = new OracleTextConditionApi({
      conditionSetId: this.conditionSetId,
      conditionName: this.conditionName,
      conditionId: this.conditionId,
      conditionValuesSetHashGetter: () => this.conditionValuesSetHash
    });
    [ this._oracleTextExpression, sliderElement ].forEach(el => { el.disabled = !this.enabled; });
    this._setInitState(this.previousElementSibling.value);

    this.#wasChanged = false;

    on(this._oracleTextExpression, 'change', () => this.onContentChange(true));
    on(this._oracleTextExpression, 'blur', () => this.onConditionChange());
    on(this._oracleTextExpression, 'keyup', event => {
      if (event.keyCode === 13) {
        this.onConditionChange();
      }
    });
  }

  _setInitState (data) {
    this.value = data.value.oracleTextExpression || '';
    this.defaultValue = data.defaultValue.oracleTextExpression || '';
    this.defaultScore = data.defaultValue.score;
    this.score = data.value.score;
    this.#currentScore = this.score;
    this._lastOracleTextExpression = this.value;
    this._lastOracleTextScore = this.score;
  }

  onContentChange (wasUpdated = true) {
    this.#wasChanged = wasUpdated;
  }

  onConditionChange () {
    // the filter controller is not availabe in workbooks
    if (!this.#wasChanged) {
      this.reportValidity();
      return;
    }
    this.filterController?.disableOtherConditionsIfNeededForConsistency([ this ]);
    const setValuesPromise = this.conditionApi.setValue(this.score, this.value);
    this.#latestSetValuesPromise = setValuesPromise;
    setValuesPromise
      .then((json) => super.onConditionChange(json))
      .catch(error => this.handleConditionValueChangeError(error))
      .finally(() => {
        this.#wasChanged = false;
        // if the changes are not fully processed yet, disable the other conditions again.
        const enable = setValuesPromise === this.#latestSetValuesPromise;
        // the filter controller is not availabe in workbooks
        this.filterController?.setConditionsDisabledForConsistency(enable, this);
      });
  }

  initChanged () {
    const valueChanged = !isEqual(this.value, this.defaultValue);
    const scoreChanged = !isEqual(this.score, this.defaultScore);
    this.changed = valueChanged || scoreChanged;
  }

  reset (triggerConditionChange = true) {
    this.score = this.defaultScore;
    this.#wasChanged = true;
    super.reset(triggerConditionChange);
  }

  _createSlider (sliderContainer) {
    this._slider = createSlider(sliderContainer, {
      animationDuration: 100,
      range: { min: MIN_ACCURACY, max: MAX_ACCURACY },
      start: MAX_ACCURACY,
      step: SLIDER_STEP,
      direction: 'rtl',
      keyboardSupport: true,
      pips: {
        mode: 'positions',
        values: [ 0, 100 ],
        density: 25,
        format: {
          to: val => {
            if (val === MIN_ACCURACY) {
              return i18n('search.fuzzy');
            } else if (val === MAX_ACCURACY) {
              return i18n('search.exact');
            } else {
              return '';
            }
          }
        }
      }
    });
    this._slider.on('change', () => {
      this.onContentChange(this.#currentScore !== this.score);
      this.#currentScore = this.score;
      this.onConditionChange();
    });

    return this._slider;
  }

  set score (score) {
    this._slider.set(score);
  }

  get score () {
    return Number(this._slider.get());
  }

  set value (oracleTextExpression) {
    this._oracleTextExpression.value = oracleTextExpression;
  }

  get value () {
    return this._oracleTextExpression.value;
  }

  get numberOfConditionValueRows () {
    return this.value ? 1 : 0;
  }

}

customElements.define('d-oracle-text-condition', OracleTextCondition);
